import { ReactNode, useRef } from 'react';

import { cx } from '@libs/classnames';
import { Dropdown } from '@ui/components/controls/Dropdown';
import { Field } from '@ui/components/controls/Field';
import { FancyBadge } from '@ui/components/FancyBadge';
import { Loader } from '@ui/components/Loader';
import { CameraIcon } from '@ui/icons/CameraIcon';
import { MicIcon } from '@ui/icons/MicIcon';

import { DeviceErrorKind, MediaDevices, MediaError } from '@features/video/hooks/useMediaDevices';
import {
  getPermissionsHelpUrl,
  getSystemHelpUrl,
  Platform,
} from '@features/video/lib/permissionsHelp';
import { MediaDeviceOption } from '@features/video/types';
import { VideoPreview } from '@features/video/ui/VideoPreview';

import { useMicLevel } from './useMicLevel';

import styles from './MediaDeviceSettings.module.css';

export type Props = {
  className?: string;
  // что нужно пользователю: превью и выбор камеры, выбор микрофона с уровнем
  video?: boolean;
  audio?: boolean;
  // подсказка под превью: что должно быть в кадре
  hint?: ReactNode;
  mirrored?: boolean;
  // трек для индикатора в эфире: выход шумодава, мьют его не касается; без него уровень с захвата
  meterTrack?: MediaStreamTrack | null;
  devices: Pick<
    MediaDevices,
    | 'status'
    | 'stream'
    | 'cams'
    | 'mics'
    | 'camId'
    | 'micId'
    | 'camError'
    | 'micError'
    | 'error'
    | 'platform'
    | 'setCamId'
    | 'setMicId'
    | 'retry'
  >;
};

// Сообщение об ошибке: заголовок, что делать, ссылка на инструкцию браузера
type Message = { title: string; text: string; help?: string };

// Настройка камеры и микрофона перед подключением: превью, выбор устройств,
// уровень микрофона. Каждое устройство пишет о своей ошибке само: камера — в превью,
// микрофон — под своим выбором; выбор остается, занятое устройство можно заменить.
// Любая кнопка «Повторить» перезапрашивает все устройства сразу
export function MediaDeviceSettings({
  className,
  video = false,
  audio = true,
  hint,
  mirrored,
  meterTrack,
  devices,
}: Props) {
  const { status, stream, cams, mics, camId, micId, setCamId, setMicId, retry } = devices;
  const { camError, micError, error, platform } = devices;
  const levelTrack = meterTrack ?? stream?.getAudioTracks()[0] ?? null;
  // ширину полоски пишем в элемент сами, минуя состояние
  const levelRef = useRef<HTMLDivElement>(null);
  useMicLevel(audio ? levelTrack : null, (level) => {
    if (levelRef.current) levelRef.current.style.width = `${level * 100}%`;
  });
  const isRequesting = status === 'requesting' || status === 'idle';
  const hasVideo = !!stream?.getVideoTracks().length;
  const hasAudio = !!stream?.getAudioTracks().length;

  // Общая ошибка объясняется в превью, у микрофона только заголовок; без превью — у микрофона
  const general = error ? describeError(error) : null;
  const camMessage =
    general ?? (camError ? describeDeviceError(camError, 'camera', platform) : null);
  const micMessage = micError
    ? describeDeviceError(micError, 'microphone', platform)
    : general
      ? video
        ? { title: general.title, text: `${general.title}.` }
        : general
      : null;
  // браузер без медиа: повторять нечего
  const canRetry = error?.kind !== 'unsupported';

  return (
    <div className={cx(styles.settings, className)}>
      {video && (
        <VideoPreview
          className={styles.settings__preview}
          stream={hasVideo ? stream : null}
          mirrored={mirrored}
          placeholder={
            camMessage ? (
              <div className={styles.settings__preview_error}>
                <CameraIcon off />
                <p className={styles.settings__preview_title}>{camMessage.title}</p>
                <p className={styles.settings__preview_text}>
                  {camMessage.text} <HelpLink href={camMessage.help} />
                </p>
                {canRetry && <FancyBadge onClick={retry}>Повторить</FancyBadge>}
              </div>
            ) : (
              <>
                <Loader preset="mobile" ignoreClubColors />
                Запрашиваем доступ к камере...
              </>
            )
          }
        />
      )}

      {video && hint && <p className={styles.settings__hint}>{hint}</p>}

      {video && (
        <Field label="Камера">
          <Dropdown
            options={toOptions(cams)}
            value={camId ?? undefined}
            placeholder={isRequesting ? 'Поиск...' : 'Нет устройств'}
            onChange={setCamId}
            disabled={!cams.length}
          />
        </Field>
      )}

      {audio && (
        <Field label="Микрофон">
          <Dropdown
            options={toOptions(mics)}
            value={micId ?? undefined}
            placeholder={isRequesting ? 'Поиск...' : 'Нет устройств'}
            onChange={setMicId}
            disabled={!mics.length}
          />
          {micMessage ? (
            <p className={styles.settings__mic_error}>
              <span>
                {micMessage.text} <HelpLink href={micMessage.help} />
              </span>
              {canRetry && (
                <FancyBadge className={styles.settings__retry} onClick={retry}>
                  {micError === 'silent' ? 'Запросить доступ' : 'Повторить'}
                </FancyBadge>
              )}
            </p>
          ) : (
            <div
              className={cx(styles.settings__level, { [styles.is_active]: hasAudio })}
              title="Уровень микрофона"
            >
              <MicIcon className={styles.settings__level_icon} off={!hasAudio} />
              <div className={styles.settings__level_bar}>
                <div ref={levelRef} className={styles.settings__level_fill} />
              </div>
            </div>
          )}
        </Field>
      )}
    </div>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Инструкция браузера в новой вкладке; без ссылки — ничего
function HelpLink({ href }: { href?: string }) {
  if (!href) return null;
  return (
    <a className={styles.settings__help} href={href} target="_blank" rel="noreferrer">
      Инструкция
    </a>
  );
}

// Тексты ошибок — предложения с точкой, заголовки — без
function sentence(text: string): string {
  return text.endsWith('.') ? text : `${text}.`;
}

function toOptions(devices: MediaDeviceOption[]) {
  return devices.map((d) => ({ value: d.deviceId, label: d.label }));
}

// Ошибка одного устройства: заголовок для превью и строка под выбором микрофона
function describeDeviceError(
  kind: DeviceErrorKind,
  device: 'camera' | 'microphone',
  platform: Platform,
): Message {
  const isCam = device === 'camera';
  switch (kind) {
    case 'denied':
      return {
        title: isCam ? 'Нет доступа к камере' : 'Нет доступа к микрофону',
        text: 'Разрешите доступ в браузере.',
        help: getPermissionsHelpUrl(device),
      };
    case 'system':
      return {
        title: isCam ? 'Камера запрещена системой' : 'Микрофон запрещен системой',
        text: `Разрешите браузеру доступ к ${isCam ? 'камере' : 'микрофону'} в настройках системы.`,
        help: getSystemHelpUrl(platform, device),
      };
    case 'busy':
      // на Windows системный запрет выглядит как занятое устройство
      if (platform === 'windows') {
        return {
          title: isCam ? 'Камера недоступна' : 'Микрофон недоступен',
          text: isCam
            ? 'Эта камера занята другим приложением или запрещена в настройках Windows.'
            : 'Этот микрофон занят другим приложением или запрещен в настройках Windows.',
          help: getSystemHelpUrl(platform, device),
        };
      }
      return isCam
        ? { title: 'Камера занята', text: 'Эта камера занята другим приложением.' }
        : { title: 'Микрофон занят', text: 'Этот микрофон занят другим приложением.' };
    case 'missing':
      return isCam
        ? { title: 'Камера не найдена', text: 'Подключите камеру.' }
        : { title: 'Микрофон не найден', text: 'Подключите микрофон.' };
    // только микрофон: захват есть, звук не запускается
    case 'silent':
      return {
        title: 'Микрофон молчит',
        text: 'Микрофон молчит: попробуйте запросить доступ еще раз.',
      };
  }
}

// Ошибка всего сразу: браузер без медиа или что-то непредвиденное
function describeError(error: MediaError): Message {
  switch (error.kind) {
    case 'unsupported':
      return {
        title: 'Браузер не поддерживает камеру и микрофон',
        text: 'Откройте страницу в другом браузере или на другом устройстве.',
      };
    case 'unknown':
      return { title: 'Не удалось получить доступ к устройствам', text: sentence(error.message) };
  }
}
